import type { ComponentType, ReactElement } from "react";
import { Loader2 } from "lucide-react";
import { useRouterState } from "@tanstack/react-router";

/**
 * 主入口页的静态首屏骨架（路由级 pending 框架）。
 *
 * 背景：顶级页面切换时，目标路由组件经 autoCodeSplitting 拆为独立 chunk，
 * 路由器在组件 chunk 就绪前不提交导航（旧页一直保留，dev 冷缓存下可达数秒）。
 * 配置 router.defaultPendingComponent 后，pendingMs 到点即提交新路由树，
 * 未就绪的组件挂起并落进该 match 自己的 Suspense 边界，本文件注册的
 * 静态骨架立即上屏，chunk 到位后真实页面原地接管。
 *
 * 约束：本模块经 App.tsx 进入主包（defaultPendingComponent 不能被拆进懒 chunk），
 * 因此这里只允许纯静态 JSX 与 lucide 图标，禁止 import 任何真实页面组件。
 * 骨架结构须与对应页面真实首屏一致（左栏宽度/标题行高度对齐），避免接管时跳变。
 */

/** pulse 占位块 */
function Bone({ className }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-md bg-muted/60 ${className ?? ""}`} />
  );
}

/** 工作台：历史面板(280px, bg-muted/30) + 右侧门户空态（居中 composer） */
function WorkbenchFrame() {
  return (
    <div className="flex h-full min-h-0" data-testid="pending-frame-workbench">
      <div className="flex h-full min-h-0 w-[280px] shrink-0 flex-col border-r border-border-muted bg-muted/30">
        <div className="flex h-12 shrink-0 items-center gap-1.5 border-b border-border-muted px-3">
          <Bone className="h-8 flex-1" />
          <Bone className="size-8 shrink-0" />
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden px-2 py-2">
          {Array.from({ length: 10 }, (_, i) => (
            <Bone key={i} className="h-9 shrink-0" />
          ))}
        </div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-4 px-8">
        <Bone className="h-7 w-72" />
        <div className="h-32 w-full max-w-3xl rounded-2xl border border-border-muted p-3">
          <Bone className="h-4 w-1/3" />
        </div>
        <div className="flex gap-2">
          <Bone className="h-8 w-28 rounded-full" />
          <Bone className="h-8 w-28 rounded-full" />
          <Bone className="h-8 w-28 rounded-full" />
        </div>
      </div>
    </div>
  );
}

/** KB 与文件中心共用形态：左侧目录栏 + 右侧内容区 */
function TreePanelFrame({
  title,
  testid,
}: {
  title: string;
  testid: string;
}) {
  return (
    <div className="flex h-full min-h-0" data-testid={testid}>
      <div
        className="relative flex shrink-0 flex-col border-r border-border-muted"
        style={{ width: 260 }}
      >
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-border-muted px-4">
          <span className="text-sm font-semibold">{title}</span>
          <Bone className="size-6" />
        </div>
        <div className="flex h-10 shrink-0 items-center px-2">
          <Bone className="h-8 flex-1" />
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-hidden px-2 py-2">
          {Array.from({ length: 12 }, (_, i) => (
            <div key={i} className="flex items-center gap-1.5">
              {i % 3 === 2 ? <div className="w-4 shrink-0" /> : null}
              <Bone className="h-7 flex-1" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border-muted px-4">
          <Bone className="h-8 w-56" />
          <Bone className="ml-auto h-8 w-24" />
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-px overflow-hidden p-3">
          {Array.from({ length: 9 }, (_, i) => (
            <Bone key={i} className="h-10 shrink-0" />
          ))}
        </div>
      </div>
    </div>
  );
}

function KbFrame() {
  return <TreePanelFrame title="知识库" testid="pending-frame-kb" />;
}

function DocCenterFrame() {
  return <TreePanelFrame title="文档" testid="pending-frame-doc-center" />;
}

/** SecondaryNav 形态（数据中心/知识本体/团队设置/系统设置）：200px 二级目录 + 内容区 */
function SecondaryNavFrame({
  title,
  rows,
  testid,
}: {
  title: string;
  rows: number;
  testid: string;
}) {
  return (
    <div className="flex h-full min-h-0 min-w-0 overflow-hidden" data-testid={testid}>
      <div className="flex w-[200px] shrink-0 flex-col border-r border-border-muted">
        <div className="flex h-12 shrink-0 items-center border-b border-border-muted px-4">
          <span className="text-sm font-semibold">{title}</span>
        </div>
        <nav className="min-h-0 flex-1 overflow-hidden p-2">
          <ul className="space-y-1">
            {Array.from({ length: rows }, (_, i) => (
              <li key={i}>
                <Bone className="h-9" />
              </li>
            ))}
          </ul>
        </nav>
      </div>
      <div className="flex min-w-0 flex-1 flex-col p-4">
        <Bone className="h-9 w-72" />
        <div className="mt-4 flex flex-col gap-2">
          {Array.from({ length: 6 }, (_, i) => (
            <Bone key={i} className="h-12" />
          ))}
        </div>
      </div>
    </div>
  );
}

/** 数据模型设计器为全屏沉浸形态（无二级目录），画布占满内容区 */
function SemanticDesignerEditFrame() {
  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-testid="pending-frame-semantic-designer"
    >
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border-muted px-4">
        <Bone className="h-8 w-40" />
        <Bone className="ml-auto h-8 w-24" />
      </div>
      <div className="min-h-0 flex-1 p-4">
        <Bone className="h-full w-full" />
      </div>
    </div>
  );
}

function SemanticPlatformFrame() {
  return (
    <SecondaryNavFrame
      title="知识本体"
      rows={14}
      testid="pending-frame-semantic-platform"
    />
  );
}

function DataCenterFrame() {
  return (
    <SecondaryNavFrame
      title="数据中心"
      rows={7}
      testid="pending-frame-data-center"
    />
  );
}

function TenantSettingsFrame() {
  return (
    <SecondaryNavFrame
      title="团队设置"
      rows={12}
      testid="pending-frame-tenant-settings"
    />
  );
}

function SettingsFrame() {
  return (
    <SecondaryNavFrame
      title="系统设置"
      rows={12}
      testid="pending-frame-settings"
    />
  );
}

/**
 * pending 框架注册表：pathname 前缀 → 骨架组件。
 * 数组序即特异性序，更具体的前缀必须排在前面（如 designer edit 在 /semantic-platform 之前）。
 */
const PENDING_FRAMES: Array<[prefix: string, Frame: ComponentType]> = [
  ["/workbench", WorkbenchFrame],
  ["/kb", KbFrame],
  ["/doc-center", DocCenterFrame],
  ["/semantic-platform/data-models/edit", SemanticDesignerEditFrame],
  ["/semantic-platform", SemanticPlatformFrame],
  ["/data-center", DataCenterFrame],
  ["/tenant-settings", TenantSettingsFrame],
  ["/settings", SettingsFrame],
];

/** 精确或「前缀 + /」匹配；/kbx 不得命中 /kb */
export function resolvePendingFrame(pathname: string): ComponentType | undefined {
  for (const [prefix, Frame] of PENDING_FRAMES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return Frame;
  }
  return undefined;
}

function DefaultLoadingFrame() {
  return (
    <div
      className="flex h-full items-center justify-center text-muted-foreground"
      data-testid="pending-frame-default"
    >
      <Loader2 className="size-6 animate-spin" />
    </div>
  );
}

/** router.defaultPendingComponent 入口：按待导航 pathname 选骨架 */
export function DefaultPendingFrame(): ReactElement {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const Frame = resolvePendingFrame(pathname);
  return Frame ? <Frame /> : <DefaultLoadingFrame />;
}
